/**
 * Support access (D-16), web and native. While signed in: read `GET /user/support-access` at start, on app
 * resume and when a push link opens `?support_access=1`, and listen on Supabase realtime for this user's
 * `support_access_requests` rows, so a request from Lightbase appears within a second. Nothing shows unless a
 * request is pending or a grant is active. Staff impersonation never sees it.
 *
 * Singleton state: the host (SupportAccessHost.vue) starts it; Settings → Account reads the grant.
 */
import { computed, ref, watch } from 'vue'
import type { RealtimeChannel } from '@supabase/supabase-js'
import { supabase } from '@/supabase'
import { getSupportAccess, respondSupportAccess, revokeSupportAccess } from '@/api/supportAccess.api'
import {
    type SupportAccessState,
    applySupportAccessRow,
    emptySupportAccessState,
    normalizeSupportAccessRow,
    pruneSupportAccessState,
} from '@/native/supportAccess'
import { onAppResume } from '@/native/lifecycle'
import { useAuthStore } from '@/stores/auth'
import { isImpersonatingClient } from '@/utils/impersonationHeaders'
import { isGuestMode } from '@/guest/guestMode'

const state = ref<SupportAccessState>(emptySupportAccessState())
const now = ref(Date.now())
const busy = ref(false)
const error = ref('')
let channel: RealtimeChannel | null = null
let currentUserId: string | null = null
let ticker: ReturnType<typeof setInterval> | null = null
let started = false

function setState(next: SupportAccessState): void {
    now.value = Date.now()
    state.value = pruneSupportAccessState(next, now.value)
    syncTicker()
}

/** Tick while something is showing, so a lapsed request or grant disappears on time. */
function syncTicker(): void {
    const live = !!(state.value.pending || state.value.active_grant)
    if (live && !ticker) {
        ticker = setInterval(() => {
            now.value = Date.now()
            const pruned = pruneSupportAccessState(state.value, now.value)
            if (pruned !== state.value) {
                state.value = pruned
                syncTicker()
            }
        }, 15_000)
    } else if (!live && ticker) {
        clearInterval(ticker)
        ticker = null
    }
}

async function refresh(): Promise<void> {
    const userId = currentUserId
    if (!userId) return
    const next = await getSupportAccess().catch(() => null)
    if (!next || currentUserId !== userId) return
    setState(next)
}

function subscribe(userId: string): void {
    unsubscribe()
    try {
        channel = supabase
            .channel(`support-access:${userId}`)
            .on(
                'postgres_changes' as any,
                { event: '*', schema: 'public', table: 'support_access_requests', filter: `user_id=eq.${userId}` },
                (payload: { new?: unknown }) => {
                    if (currentUserId !== userId) return
                    const row = normalizeSupportAccessRow(payload?.new)
                    if (row) setState(applySupportAccessRow(state.value, row))
                },
            )
            .subscribe((status: string) => {
                // (Re)joined: catch anything that changed while the socket was down.
                if (status === 'SUBSCRIBED') void refresh()
            })
    } catch {
        channel = null
    }
}

function unsubscribe(): void {
    if (!channel) return
    try {
        void supabase.removeChannel(channel)
    } catch {
        /* ignore */
    }
    channel = null
}

function enabledFor(userId: string | null): boolean {
    return !!userId && !isImpersonatingClient() && !isGuestMode()
}

export default function useSupportAccess() {
    const auth = useAuthStore()

    const pending = computed(() => state.value.pending)
    const activeGrant = computed(() => state.value.active_grant)

    function start(): void {
        if (started) return
        started = true
        watch(
            () => (auth.isAuthenticated ? String(auth.currentUser?.id ?? '') || null : null),
            (userId) => {
                currentUserId = enabledFor(userId) ? userId : null
                setState(emptySupportAccessState())
                if (!currentUserId) {
                    unsubscribe()
                    return
                }
                subscribe(currentUserId)
                void refresh()
            },
            { immediate: true },
        )
        onAppResume(() => void refresh())
    }

    async function respond(approve: boolean): Promise<boolean> {
        const request = state.value.pending
        if (!request || busy.value) return false
        busy.value = true
        error.value = ''
        try {
            const row = await respondSupportAccess(request.id, approve).catch(() => null)
            if (!row) {
                error.value = 'Something went wrong. Please try again.'
                // The request may have expired meanwhile: re-read the truth.
                void refresh()
                return false
            }
            setState(applySupportAccessRow(state.value, row))
            return true
        } finally {
            busy.value = false
        }
    }

    async function revoke(): Promise<boolean> {
        if (busy.value) return false
        busy.value = true
        error.value = ''
        try {
            const ok = await revokeSupportAccess().catch(() => false)
            if (!ok) {
                error.value = 'Couldn’t revoke access. Please try again.'
                return false
            }
            setState({ ...state.value, active_grant: null })
            void refresh()
            return true
        } finally {
            busy.value = false
        }
    }

    return {
        pending,
        activeGrant,
        now,
        busy,
        error,
        start,
        refresh,
        allow: () => respond(true),
        deny: () => respond(false),
        revoke,
    }
}
